// Sidebar.tsx - Updated with permission-based visibility
import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaNewspaper,
  FaCalendarAlt,
  FaUsers,
  FaChartBar,
  FaCog,
  FaChevronRight,
  FaUserCircle,
  FaChevronLeft,
  FaChevronRight as FaChevronRightIcon,
  FaLifeRing
} from '../../utils/iconUtils';
import { MdLibraryBooks } from 'react-icons/md';

interface MenuItem {
  path: string;
  label: string;
  icon: React.ComponentType<any>;
  description: string;
  badge?: number;
  requiredRole?: 'super_admin' | 'admin' | 'moderator' | 'user';
  requiredSection?: string; // Section to check for view permission
}

const Sidebar: React.FC<{ 
  isCollapsed: boolean; 
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>> 
}> = ({ isCollapsed, setIsCollapsed }) => {
  const { 
    currentUser, 
    canViewSection,
    canPerformAction,
     canViewSidebarItem,
    hasRole, 
    userProfile 
  } = useAuth();


  // Define all possible menu items with their permission requirements
 // Define all possible menu items
  const allMenuItems: MenuItem[] = [
  
    { 
      path: '/admin/library', 
      label: 'Library', 
      icon: MdLibraryBooks,
      description: 'Resources and documents',
      requiredSection: 'library'
    },
    { 
      path: '/admin/news', 
      label: 'News Management', 
      icon: FaNewspaper,
      description: 'Articles and announcements',
      requiredSection: 'news'
    },
    { 
      path: '/admin/programs', 
      label: 'Programs', 
      icon: FaCalendarAlt,
      description: 'Events and schedules',
      requiredSection: 'programs'
    },
    { 
      path: '/admin/users', 
      label: 'User Management', 
      icon: FaUsers,
      description: 'Members and permissions',
      requiredRole: 'super_admin'
    },
    { 
      path: '/admin/accounting', 
      label: 'Accounting', 
      icon: FaChartBar,
      description: 'Financial records',
      requiredSection: 'accounting'
    },
    { 
      path: '/admin/settings', 
      label: 'Settings', 
      icon: FaCog,
      description: 'System configuration',
      requiredRole: 'super_admin'
    },
    { 
      path: '/admin/support', 
      label: 'Support', 
      icon: FaLifeRing,
      description: 'Help and documentation'
    },
 
  
  ];

// Update the menu items filtering logic
  const menuItems = allMenuItems.filter(item => {
    // If user has an explicit sidebarVisibility override for this path, use it
    const explicit = userProfile?.permissions?.sidebarVisibility?.[item.path];
    if (explicit !== undefined) return explicit;

    if (item.requiredRole) {
      return hasRole(item.requiredRole);
    }
    if (item.requiredSection) {
      return canViewSection(item.requiredSection) || canPerformAction(item.requiredSection, 'view');
    }
    // Fallback to computed helper
    return canViewSidebarItem(item.path);
  });




  // Get accessible sections count for user info
  const getAccessibleSectionsCount = () => {
    if (!userProfile?.permissions) return 0;
    const sections = ['news', 'library', 'programs', 'accounting', 'users', 'settings'];
    return sections.filter(section => canViewSection(section)).length;
  };

  // Get permission level badge
  const getPermissionLevelBadge = () => {
    if (!currentUser?.role) return null;
    
    switch (currentUser.role) {
      case 'super_admin':
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gradient-to-r from-purple-500 to-purple-700 text-white">
            Full Access
          </span>
        );
      case 'admin':
        if (userProfile?.permissionPreset === 'editor') {
          return (
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gradient-to-r from-green-500 to-green-700 text-white">
              Editor
            </span>
          );
        } else if (userProfile?.permissionPreset === 'viewer') {
          return (
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gradient-to-r from-gray-500 to-gray-700 text-white">
              Viewer
            </span>
          );
        }
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gradient-to-r from-red-500 to-red-700 text-white">
            Admin
          </span>
        );
      case 'moderator':
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gradient-to-r from-blue-500 to-blue-700 text-white">
            Moderator
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gradient-to-r from-gray-500 to-gray-700 text-white">
            User
          </span>
        );
    }
  };

  const getRoleColor = (role?: string) => {
    switch (role) {
      case 'super_admin': return 'bg-gradient-to-br from-purple-600 to-purple-800';
      case 'admin': return 'bg-gradient-to-br from-red-600 to-red-800';
      case 'moderator': return 'bg-gradient-to-br from-blue-600 to-blue-800';
      default: return 'bg-gradient-to-br from-gray-600 to-gray-800';
    }
  };

  return (
    <div 
      className={`h-full flex flex-col border-r border-slate-900/20 bg-slate-950 text-white shadow-2xl shadow-slate-950/20 transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo and Brand */}
      <div className="relative border-b border-white/10 p-3">
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-2.5'}`}>
          <div className="h-8 w-8 rounded-md bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shrink-0 shadow-sm">
            <span className="text-sm font-bold text-slate-950">A</span>
          </div>
          {!isCollapsed && (
            <div className="overflow-hidden">
              <h1 className="text-sm font-semibold text-white tracking-tight whitespace-nowrap">Admin Portal</h1>
              <p className="text-[10px] text-slate-400 whitespace-nowrap">v2.1.0</p>
            </div>
          )}
        </div>
        
        {/* Collapse Toggle Button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={`absolute top-1/2 -translate-y-1/2 ${
            isCollapsed ? 'right-1.5' : 'right-2'
          } rounded-full bg-white/10 p-1.5 text-slate-200 transition-colors hover:bg-white/15 z-10`}
        >
          {isCollapsed ? (
            <FaChevronRightIcon className="h-3 w-3" />
          ) : (
            <FaChevronLeft className="h-3 w-3" />
          )}
        </button>
      </div>

      {/* User Profile - Enhanced with permissions */}
      <div className="border-b border-white/10 p-3">
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-2.5'}`}>
              {(() => {
                const avatarUrl = (userProfile as any)?.photoURL || (currentUser as any)?.photoURL || (userProfile as any)?.avatar || undefined;
                if (avatarUrl) {
                  return (
                    <img
                      src={avatarUrl}
                      alt="User avatar"
                      className={`h-8 w-8 rounded-md object-cover shrink-0 ${getRoleColor(currentUser?.role)}`}
                    />
                  );
                }

                return (
                  <div className={`h-8 w-8 rounded-md ${getRoleColor(currentUser?.role)} flex items-center justify-center shrink-0`}>
                    <FaUserCircle className="h-5 w-5" />
                  </div>
                );
              })()}
          {!isCollapsed && (
            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="text-xs font-medium truncate text-slate-100">{currentUser?.email || 'Administrator'}</p>
              <div className="flex items-center justify-between mt-1 gap-2">
                <p className="text-[10px] text-slate-400 capitalize">{currentUser?.role || 'Admin'}</p>
                {getPermissionLevelBadge()}
              </div>
              <div className="flex items-center mt-1">
                <span className="text-[10px] text-slate-500">
                  {getAccessibleSectionsCount()} accessible sections
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

   {/* Navigation */}
<nav className="flex-1 overflow-y-auto p-2 space-y-1">
  {menuItems.map((item) => {
    const Icon = item.icon;

    return (
      <NavLink
        key={item.path}
        to={item.path}
        className={({ isActive }) =>
          `relative group flex items-center ${
            isCollapsed ? 'justify-center px-2' : 'justify-between px-2.5'
          } py-2 rounded-md text-sm transition-all duration-200 ${
            isActive
              ? 'bg-white text-slate-950 shadow-sm'
              : 'text-slate-300 hover:bg-white/10 hover:text-white'
          }`
        }
        title={isCollapsed ? `${item.label} - ${item.description}` : undefined}
      >
        {({ isActive }) => (
          <>
            <div className="flex items-center space-x-2.5">
              <div
                className={`p-1.5 rounded-md shrink-0 ${
                  isActive
                    ? 'bg-slate-100 text-primary-700'
                    : 'bg-white/10 text-slate-300 group-hover:bg-white/15 group-hover:text-white'
                }`}
              >
                <Icon className="h-4 w-4" />
              </div>

              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-medium truncate">{item.label}</p>

                    {item.badge && item.badge > 0 && (
                      <span className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-rose-500 text-white shrink-0">
                        {item.badge}
                      </span>
                    )}
                  </div>

                  <p className={`text-[10px] mt-0.5 truncate ${isActive ? 'text-slate-500' : 'text-slate-500 group-hover:text-slate-400'}`}>
                    {item.description}
                  </p>
                </div>
              )}
            </div>

            {/* Chevron */}
            {!isCollapsed && (
              <FaChevronRight
                className={`h-3 w-3 transition-transform shrink-0 ${
                  isActive ? 'rotate-90 text-slate-500' : 'text-slate-500 group-hover:translate-x-0.5'
                }`}
              />
            )}

            {/* Badge dot when collapsed */}
            {isCollapsed && item.badge && item.badge > 0 && (
              <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-red-500" />
            )}
          </>
        )}
      </NavLink>
    );
  })}
</nav>

      {/* Footer Section */}
      <div className="border-t border-white/10 p-3">
        {!isCollapsed && (
          <div className="mb-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] text-slate-400">Access Level</span>
              <span className={`text-xs px-2 py-1 rounded-full ${
                currentUser?.role === 'super_admin' 
                  ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50'
                  : userProfile?.permissionPreset === 'editor'
                  ? 'bg-green-500/30 text-green-300 border border-green-500/50'
                  : 'bg-blue-500/30 text-blue-300 border border-blue-500/50'
              }`}>
                {currentUser?.role === 'super_admin' ? 'Full Control' : 
                 userProfile?.permissionPreset === 'editor' ? 'Editor' : 'Viewer'}
              </span>
            </div>
            
            <div className="flex items-center justify-between text-[10px] text-slate-500">
              <span>{menuItems.length} accessible sections</span>
              <span>
                {new Date().toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </span>
            </div>
          </div>
        )}

        {/* Logout Button */}
        {/* <button
          onClick={handleLogout}
          className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} p-3 rounded-lg bg-primary-700 hover:bg-primary-600 transition-colors`}
          title={isCollapsed ? 'Logout' : undefined}
        >
          <div className="flex items-center space-x-3">
            <FaSignOutAlt className="h-5 w-5" />
            {!isCollapsed && <span>Logout</span>}
          </div>
        </button> */}
      </div>
    </div>
  );
};

export default Sidebar;
